<template>
  <v-container fluid>
    <v-row>
      <v-col>
        <FormHotel
          v-if="hotel"
          :data="hotel"
          :loading="isLoading"
          :disabled="isMutationLoading"
          :isNew="!id"
          :language="language"
          :cities="cities"
          :types="types"
          :facilities="facilities"
          :amenities="amenities"
          :tabs="allowedTabs"
          :currentTab="tab"
          :reservations-count="reservationsCount"
          @cancel="$router.push({ name: 'page-hotels' })"
          @delete-hotel="deleteHotel"
          @language-update="language = $event"
          @remove-room="deleteRoom"
          @remove-room-type="deleteRoomType"
          @submit="hotelSubmitHandle"
          @onwards="onwardsHandler"
          @change:descriptionForMobile="changeDescForMobHandler($event)"
          @change:shortDescriptionForMobile="changeShortDescForMobHandler($event)"
        />
      </v-col>
    </v-row>
    <ConfirmationDialog ref="confirmHotelRemoving" confirmText="Удалить" />
  </v-container>
</template>

<script>
import ConfirmationDialog from '@/components/confirmation-dialog'
import FormHotel from '@/pages/hotel/form'
import hotelQuery from '@/services/gql/types/Hotels/queries/hotel.gql'
import updateHotelMutation from '@/services/gql/types/Hotels/mutations/updateHotel.gql'
import createHotelMutation from '@/services/gql/types/Hotels/mutations/createHotel.gql'
import deleteHotelMutation from '@/services/gql/types/Hotels/mutations/deleteHotel.gql'
import updateRoomTypeMutation from '@/services/gql/types/RoomTypes/mutations/updateRoomType.gql'
import createRoomTypeMutation from '@/services/gql/types/RoomTypes/mutations/createRoomType.gql'
import deleteRoomTypeMutation from '@/services/gql/types/RoomTypes/mutations/deleteRoomType.gql'
import updateRoomMutation from '@/services/gql/types/Rooms/mutations/updateRoom.gql'
import createRoomMutation from '@/services/gql/types/Rooms/mutations/createRoom.gql'
import deleteRoomMutation from '@/services/gql/types/Rooms/mutations/deleteRoom.gql'
import dictionaryOptionsQuery from '@/services/gql/types/DictionaryOptions/queries/dictionaryOptions.gql'
import { contentLocales, contentLocalesValues } from '@/configs/locales'
import citiesQuery from '@/services/gql/types/City/queries/citiesGrid.gql'
import { getListWithNewMainLanguage, setTranslation } from '@/utils/locales'
import hotelReservationsCountQuery from '@/services/gql/types/HotelReservations/queries/hotelReservationsCount.gql'

const tabs = [
  {
    title: 'Отель',
    value: 'hotel',
    disabled: false
  },
  {
    title: 'Типы комнат',
    value: 'roomTypes',
    disabled: true
  },
  {
    title: 'Комнаты',
    value: 'rooms',
    disabled: true
  }
]

export default {
  name: 'PageHotel',
  props: {
    id: {
      type: [Number, String],
      default: null
    },
    categorySlug: {
      type: [Number, String],
      default: null
    }
  },
  tabs,
  components: {
    FormHotel,
    ConfirmationDialog
  },
  metaInfo () {
    return {
      title: this.id ? 'Редактировать отель' : 'Создать отель'
    }
  },
  data () {
    return {
      language: contentLocales[0].value,
      hotel: {},
      cities: [],
      types: [],
      facilities: [],
      amenities: [],
      isFormLoading: false,
      allowedTabs: tabs,
      tab: 0,
      reservationsCount: 0
    }
  },
  computed: {
    isLoading () {
      return (
        this.isFormLoading ||
        this.$apollo.queries.hotel.loading
      )
    },
    isMutationLoading () {
      return this.$async.createHotel.$pending || this.$async.updateHotel.$pending
    }
  },
  methods: {
    refetchHotel () {
      this.$apollo.queries.hotel.refetch()
    },
    changeDescForMobHandler ({ fields, $event }) {
      fields.descriptionForMobile = $event
    },
    changeShortDescForMobHandler ({ fields, $event }) {
      fields.shortDescriptionForMobile = $event
    },
    async deleteHotel () {
      try {
        await this.$refs.confirmHotelRemoving.open()
        await this.$async.deleteHotel.$perform({ id: this.id })
        this.$successMsg('Отель удален')
      } catch (e) {
        if (e.cancel) return
        this.$handleApiError(e.graphQLErrors, `Ошибка при удалении отеля с ID ${this.id}`)
      } finally {
        await this.$router.push({ name: 'page-hotels' })
      }
    },
    async hotelSubmitHandle (data) {
      if (this.id) await this.updateHotel(data)
      else {
        await this.createHotel(data)
      }
    },
    async createHotel (data) {
      try {
        delete data?.translations
        this.isFormLoading = true
        const roomTypes = data?.roomTypes ? [...data?.roomTypes] : null
        const rooms = data?.rooms ? [...data?.rooms] : null
        delete data?.roomTypes
        delete data?.rooms
        const {
          data: { createHotel }
        } = await this.$async.createHotel.$perform({ data })
        // create roomTypes
        const createRoomTypesPromises = []
        const updateRoomTypesPromises = []
        for (const roomType of roomTypes) {
          createRoomTypesPromises.push(
            await this.createRoomType(roomType, createHotel.id)
          )
        }
        await Promise.all(createRoomTypesPromises)
        await Promise.all(updateRoomTypesPromises)
        // update rooms
        const createRoomPromises = []
        for (const room of rooms) {
          createRoomPromises.push(await this.createRoom(room))
        }
        await Promise.all(createRoomPromises)
        this.$successMsg('Отель создан')
        this.isFormLoading = false
        this.$emit('updated')
        await this.$router.push({
          name: 'page-hotel',
          params: {
            id: Number(createHotel.id)
          }
        })
      } catch (e) {
        this.$handleApiError(e.graphQLErrors, 'Ошибка при создании отеля')
        this.isFormLoading = false
      }
    },
    async createRoom (data) {
      try {
        await this.$async.createRoom.$perform({
          id: data.id,
          data
        })
      } catch (e) {
        this.$handleApiError(e.graphQLErrors, 'Ошибка при создании типа комнаты')
      }
    },
    async deleteRoom (id) {
      try {
        await this.$async.deleteRoom.$perform({ id })
        this.$successMsg('Комната удалена')
        this.refetchHotel()
      } catch (e) {
        this.$handleApiError(e.graphQLErrors, 'Ошибка при удалении комнаты')
      }
    },
    async updateRoom (data, id) {
      try {
        delete data.id
        await this.$async.updateRoom.$perform({
          id,
          data
        })
      } catch (e) {
        this.$handleApiError(e.graphQLErrors, 'Ошибка при обновлении типа комнаты')
      }
    },
    async createRoomType (data, hotelId) {
      try {
        await this.$async.createRoomType.$perform({
          id: data.id,
          data: {
            hotelId,
            ...data
          }
        })
      } catch (e) {
        this.$handleApiError(e.graphQLErrors, 'Ошибка при создании типа комнаты')
      }
    },
    async deleteRoomType (id) {
      try {
        await this.$async.deleteRoomType.$perform({ id })
        this.$successMsg('Тип комнаты удален')
        this.refetchHotel()
      } catch (e) {
        this.$handleApiError(e.graphQLErrors, 'Ошибка при удалении типа комнаты')
      }
    },
    async updateRoomType (data, id) {
      try {
        delete data.id
        await this.$async.updateRoomType.$perform(
          {
            id,
            data
          },
          { Language: getListWithNewMainLanguage(this.language, contentLocalesValues()) }
        )
      } catch (e) {
        this.$handleApiError(e.graphQLErrors, 'Ошибка при обновлении типа комнаты')
      }
    },
    async updateHotel (data) {
      try {
        setTranslation(data, this.language)
        this.isFormLoading = true
        const roomTypes = [...data.roomTypes]
        const rooms = [...data.rooms]
        delete data.roomTypes
        delete data.rooms
        const {
          data: { updateHotel }
        } = await this.$async.updateHotel.$perform(
          {
            id: Number(this.id),
            data
          },
          {
            Language: getListWithNewMainLanguage(this.language, contentLocalesValues())
          }
        )
        // update roomTypes
        if (roomTypes?.length) {
          const createRoomTypesPromises = []
          const updateRoomTypesPromises = []
          for (const roomType of roomTypes) {
            if (roomType.id) {
              // roomType.hotelId = updateHotel.id
              roomType.translations = data.translations
              updateRoomTypesPromises.push(
                await this.updateRoomType(roomType, roomType.id)
              )
            } else {
              createRoomTypesPromises.push(
                await this.createRoomType(roomType, updateHotel.id)
              )
            }
          }
          await Promise.all(createRoomTypesPromises)
          await Promise.all(updateRoomTypesPromises)
        }

        // update rooms
        if (rooms?.length) {
          const createRoomPromises = []
          const updateRoomPromises = []
          for (const room of rooms) {
            if (room.id) {
              updateRoomPromises.push(await this.updateRoom(room, room.id))
            } else {
              createRoomPromises.push(await this.createRoom(room))
            }
          }
          await Promise.all(createRoomPromises)
          await Promise.all(updateRoomPromises)
        }
        this.$successMsg('Данные отеля обновлены')
        this.isFormLoading = false
        this.$emit('updated')
        this.refetchHotel()
      } catch (e) {
        this.$handleApiError(e.graphQLErrors, 'Ошибка при обновлении данных отеля')
        this.isFormLoading = false
      }
    },
    async onwardsHandler ({ activeTab, payload }) {
      await this.hotelSubmitHandle(payload)
      this.allowedTabs[activeTab + 1].disabled = false
      this.tab = activeTab + 1
    }
  },
  apollo: {
    cities: {
      fetchPolicy: 'network-only',
      query: citiesQuery,
      pollInterval: 30000,
      variables: {
        where: {
          status_not: 'DRAFT'
        }
      },
      result ({ data }) {
        this.cities = data?.cities?.list
      }
    },
    types: {
      fetchPolicy: 'network-only',
      manual: true,
      query: dictionaryOptionsQuery,
      variables: {
        where: { dictionary: { name: 'hotelType' } }
      },
      result ({ data }) {
        this.types = data?.dictionaryOptions?.list
      }
    },
    facilities: {
      fetchPolicy: 'network-only',
      manual: true,
      query: dictionaryOptionsQuery,
      variables: {
        where: { dictionary: { name: 'hotelFacility' } }
      },
      result ({ data }) {
        this.facilities = data?.dictionaryOptions?.list
      }
    },
    amenities: {
      fetchPolicy: 'network-only',
      manual: true,
      query: dictionaryOptionsQuery,
      variables: {
        where: { dictionary: { name: 'roomAmenity' } }
      },
      result ({ data }) {
        this.amenities = data?.dictionaryOptions?.list
      }
    },
    hotel: {
      fetchPolicy: 'network-only',
      query: hotelQuery,
      skip () {
        return !this.id
      },
      variables () {
        return {
          where: {
            id: Number(this.id)
          }
        }
      },
      context () {
        return {
          headers: {
            Language: getListWithNewMainLanguage(this.language, contentLocalesValues())
          }
        }
      },
      result (res) {
        this.hotel = res.data.hotel
      }
    },
    reservations: {
      fetchPolicy: 'network-only',
      query: hotelReservationsCountQuery,
      skip () {
        return !this.hotel?.id
      },
      variables () {
        return {
          where: {
            room: { roomType: { hotel: { id: this.hotel.id } } }
          }
        }
      },
      update ({ reservations }) {
        this.reservationsCount = reservations.totalCount
        return reservations.totalCount
      }
    }
  },
  async: {
    deleteHotel (variables) {
      return this.$apollo.mutate({
        mutation: deleteHotelMutation,
        variables
      })
    },
    createHotel (variables) {
      return this.$apollo.mutate({
        mutation: createHotelMutation,
        variables
      })
    },
    updateHotel (variables, headers) {
      return this.$apollo.mutate({
        mutation: updateHotelMutation,
        variables,
        context: {
          headers
        }
      })
    },
    createRoomType (variables) {
      return this.$apollo.mutate({
        mutation: createRoomTypeMutation,
        variables
      })
    },
    deleteRoomType (variables) {
      return this.$apollo.mutate({
        mutation: deleteRoomTypeMutation,
        variables
      })
    },
    updateRoomType (variables, headers) {
      return this.$apollo.mutate({
        mutation: updateRoomTypeMutation,
        variables,
        context: {
          headers
        }
      })
    },
    createRoom (variables) {
      return this.$apollo.mutate({
        mutation: createRoomMutation,
        variables
      })
    },
    deleteRoom (variables) {
      return this.$apollo.mutate({
        mutation: deleteRoomMutation,
        variables
      })
    },
    updateRoom (variables) {
      return this.$apollo.mutate({
        mutation: updateRoomMutation,
        variables
      })
    }
  },
  mounted () {
    if (this?.id) {
      this.allowedTabs = this.allowedTabs.map(tab => ({
        ...tab,
        disabled: false
      }))
    }
  }
}
</script>
